"use client";

import * as React from "react";
import { Clock } from "lucide-react";
import { constructFrom, startOfDay } from "date-fns";

import { cn } from "../../lib/utils";
import { Input } from "./input";
import { Label } from "./label";
import {
	type Period,
	type TimePickerType,
	display12HourValue,
	getArrowByType,
	getDateByType,
	setDateByType,
} from "./time-picker-utils";

// ---------------------------------------------------------------------------
// TimePickerInput
// ---------------------------------------------------------------------------

interface TimePickerInputProps
	extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
	picker: TimePickerType;
	date: Date | undefined;
	setDate: (date: Date | undefined) => void;
	period?: Period;
	onRightFocus?: () => void;
	onLeftFocus?: () => void;
}

const TimePickerInput = React.forwardRef<
	HTMLInputElement,
	TimePickerInputProps
>(
	(
		{
			className,
			type = "tel",
			value,
			id,
			name,
			date = new Date(new Date().setHours(0, 0, 0, 0)),
			setDate,
			onChange,
			onKeyDown,
			picker,
			period,
			onLeftFocus,
			onRightFocus,
			...props
		},
		ref,
	) => {
		const [flag, setFlag] = React.useState<boolean>(false);
		const [prevIntKey, setPrevIntKey] = React.useState<string>("0");

		React.useEffect(() => {
			if (flag) {
				const timer = setTimeout(() => {
					setFlag(false);
				}, 2000);

				return () => clearTimeout(timer);
			}
		}, [flag]);

		const calculatedValue = React.useMemo(
			() => getDateByType(date, picker),
			[date, picker],
		);

		const calculateNewValue = (key: string) => {
			if (picker === "12hours") {
				if (flag && calculatedValue.slice(1, 2) === "1" && prevIntKey === "0") {
					return "0" + key;
				}
			}

			return !flag ? "0" + key : calculatedValue.slice(1, 2) + key;
		};

		const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === "Tab") {
				return;
			}
			e.preventDefault();
			if (e.key === "ArrowRight") {
				onRightFocus?.();
			}
			if (e.key === "ArrowLeft") {
				onLeftFocus?.();
			}
			if (["ArrowUp", "ArrowDown"].includes(e.key)) {
				const step = e.key === "ArrowUp" ? 1 : -1;
				const newValue = getArrowByType(calculatedValue, step, picker);
				if (flag) {
					setFlag(false);
				}
				const tempDate = constructFrom(date, date);
				setDate(setDateByType(tempDate, newValue, picker, period));
			}
			if (e.key >= "0" && e.key <= "9") {
				if (picker === "12hours") {
					setPrevIntKey(e.key);
				}

				const newValue = calculateNewValue(e.key);
				if (flag) {
					onRightFocus?.();
				}
				setFlag((prev) => !prev);
				const tempDate = constructFrom(date, date);
				setDate(setDateByType(tempDate, newValue, picker, period));
			}
		};

		return (
			<Input
				ref={ref}
				id={id || picker}
				name={name || picker}
				className={cn(
					"w-[48px] text-center font-mono text-base tabular-nums caret-transparent focus:bg-accent focus:text-accent-foreground [&::-webkit-inner-spin-button]:appearance-none",
					className,
				)}
				value={value || calculatedValue}
				onChange={(e) => {
					e.preventDefault();
					onChange?.(e);
				}}
				type={type}
				inputMode="decimal"
				onKeyDown={(e) => {
					onKeyDown?.(e);
					handleKeyDown(e);
				}}
				{...props}
			/>
		);
	},
);
TimePickerInput.displayName = "TimePickerInput";

// ---------------------------------------------------------------------------
// PeriodInput
// ---------------------------------------------------------------------------

interface PeriodInputProps {
	period: Period;
	setPeriod: (m: Period) => void;
	date: Date | undefined;
	setDate: (date: Date | undefined) => void;
	onRightFocus?: () => void;
	onLeftFocus?: () => void;
}

const PeriodInput = React.forwardRef<HTMLInputElement, PeriodInputProps>(
	({ period, setPeriod, date, setDate, onLeftFocus, onRightFocus }, ref) => {
		const updateDateWithNewPeriod = (newPeriod: Period) => {
			if (!date) {
				return;
			}

			const tempDate = constructFrom(date, date);
			const hours = display12HourValue(date.getHours());
			const updatedDate = setDateByType(
				tempDate,
				hours.toString(),
				"12hours",
				newPeriod,
			);
			setDate(updatedDate);
		};

		const changePeriod = (newPeriod: Period) => {
			setPeriod(newPeriod);
			updateDateWithNewPeriod(newPeriod);
		};

		const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === "Tab") {
				return;
			}

			e.preventDefault();

			if (e.key === "ArrowRight") {
				onRightFocus?.();
				return;
			}
			if (e.key === "ArrowLeft") {
				onLeftFocus?.();
				return;
			}

			if (e.key === "ArrowUp" || e.key === "ArrowDown") {
				const newPeriod = period === "AM" ? "PM" : "AM";
				changePeriod(newPeriod);
				return;
			}

			const key = e.key.toLowerCase();
			if (key === "a" || key === "1") {
				changePeriod("AM");
				return;
			}
			if (key === "p" || key === "2") {
				changePeriod("PM");
				return;
			}
		};

		const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
			e.target.select();
		};

		return (
			<Input
				id="period"
				ref={ref}
				type="text"
				value={period}
				onChange={(e) => e.preventDefault()}
				onKeyDown={handleKeyDown}
				onFocus={handleFocus}
				className={cn(
					"w-[48px] text-center font-mono text-base tabular-nums caret-transparent focus:bg-accent focus:text-accent-foreground [&::-webkit-inner-spin-button]:appearance-none",
				)}
				maxLength={2}
				placeholder="AM"
				autoComplete="off"
				spellCheck={false}
			/>
		);
	},
);
PeriodInput.displayName = "PeriodInput";

// ---------------------------------------------------------------------------
// TimePicker12Hours
// ---------------------------------------------------------------------------

export type TimePickerDefaultTime = "now" | "startOfDay" | "nearestInterval";

export const getDateWithDefaultTime = (
	date: Date,
	defaultTime: TimePickerDefaultTime,
): Date => {
	// `constructFrom` preserves the input's class/timezone (e.g. a `TZDate`), so times set below land
	// in the date's own zone rather than the browser's.
	const selectedDate = constructFrom(date, date);

	switch (defaultTime) {
		case "startOfDay":
			return startOfDay(date);
		case "nearestInterval": {
			const now = constructFrom(date, Date.now());
			const intervalMinutes = 5;
			const currentMinutes = now.getMinutes();
			const currentSeconds = now.getSeconds();

			const totalMinutes = currentMinutes + (currentSeconds >= 30 ? 1 : 0);
			const nearestInterval =
				Math.ceil(totalMinutes / intervalMinutes) * intervalMinutes;

			const finalHours = now.getHours() + Math.floor(nearestInterval / 60);
			const finalMinutes = nearestInterval % 60;

			selectedDate.setHours(finalHours, finalMinutes, 0, 0);
			return selectedDate;
		}
		case "now": {
			const now = constructFrom(date, Date.now());
			selectedDate.setHours(now.getHours(), now.getMinutes(), 0, 0);
			return selectedDate;
		}
		default:
			return date;
	}
};

const getPeriodFromDate = (date: Date | undefined): Period => {
	if (!date) {
		return "AM";
	}
	return date.getHours() >= 12 ? "PM" : "AM";
};

type TimePickerProps = {
	date: Date | undefined;
	setDate: (date: Date | undefined) => void;
	defaultTime: TimePickerDefaultTime;
};

type TimePickerHandle = Pick<HTMLInputElement, "focus">;

const TimePicker12Hours = React.forwardRef<TimePickerHandle, TimePickerProps>(
	function TimePicker12Hours(
		{ date, setDate, defaultTime: defaultTimeConfig }: TimePickerProps,
		ref,
	) {
		const defaultTime =
			date ?? getDateWithDefaultTime(new Date(), defaultTimeConfig);

		const [period, setPeriod] = React.useState<Period>(
			getPeriodFromDate(date ?? defaultTime),
		);

		const hourRef = React.useRef<HTMLInputElement>(null);
		const minuteRef = React.useRef<HTMLInputElement>(null);
		const periodRef = React.useRef<HTMLInputElement>(null);

		React.useImperativeHandle(
			ref,
			() => ({
				focus() {
					hourRef.current?.focus();
				},
			}),
			[],
		);

		return (
			<div data-testid="time-picker-input" className="flex gap-x-2 items-end">
				<div className="grid text-center">
					<Label htmlFor="hours" className="text-xs">
						Hours
					</Label>
					<TimePickerInput
						picker="12hours"
						period={period}
						date={date ?? defaultTime}
						setDate={setDate}
						ref={hourRef}
						onRightFocus={() => minuteRef.current?.focus()}
					/>
				</div>

				<div className="grid text-center">
					<Label htmlFor="minutes" className="text-xs">
						Minutes
					</Label>
					<TimePickerInput
						picker="minutes"
						id="minutes12"
						date={date ?? defaultTime}
						setDate={setDate}
						ref={minuteRef}
						onLeftFocus={() => hourRef.current?.focus()}
						onRightFocus={() => periodRef.current?.focus()}
					/>
				</div>

				<div className="grid text-center">
					<Label htmlFor="period" className="text-xs">
						Period
					</Label>
					<PeriodInput
						period={period}
						setPeriod={setPeriod}
						date={date ?? defaultTime}
						setDate={setDate}
						ref={periodRef}
						onLeftFocus={() => minuteRef.current?.focus()}
					/>
				</div>

				<Clock className="h-4 w-4 mb-2 text-neutral-400" />
			</div>
		);
	},
);

export { TimePickerInput, PeriodInput, TimePicker12Hours };
export type { TimePickerHandle };
